/**
 * Schedule type definitions - aligned with M365App backend
 * These match the PlaylistScheduleBase.cs enums exactly
 */

// ============================================================================
// ENUMS (Match Backend Exactly)
// ============================================================================

export enum ScheduleType {
  Always = 0,
  TimeWindow = 1,
  PrayerWindow = 2,
  CrossDayTimeWindow = 3,
  CrossDayPrayerWindow = 4,
  HijriDate = 5,
  GregorianDate = 6,
  PrayerBased = 7,
  EventBased = 8, // special type for Events playlist
}

export enum RepeatType {
  None = 0,
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
  Yearly = 4,
}

export enum PriorityLevel {
  Lowest = 0,
  Low = 1,
  Normal = 2,
  High = 3,
  Highest = 4,
  Critical = 5,
}

export enum PrayerName {
  StartFajr = 0,
  StartSunrise = 1,
  StartNoon = 2,
  StartDhuhr = 3,
  StartAsr = 4,
  StartMaghrib = 5,
  StartEsha = 6,
  JamFajr = 7,
  JamDhuhr = 8,
  JamAsar = 9,
  JamMaghrib = 10,
  JamEsha = 11,
  Jummah1 = 12,
  Jummah2 = 13,
  Jummah3 = 14,
  Jummah4 = 15,
}

// ============================================================================
// BASE SCHEDULE INTERFACE
// ============================================================================

export interface ScheduleBase {
  type: ScheduleType;
  repeat: RepeatType;
  scheduleType: string; // Discriminator property from backend
}

// ============================================================================
// SCHEDULE TYPE INTERFACES (8 Types)
// ============================================================================

/**
 * 1. Always Active Schedule
 * Shows playlist continuously without restrictions
 */
export interface AlwaysSchedule extends ScheduleBase {
  scheduleType: "AlwaysSchedule";
  type: ScheduleType.Always;
}

/**
 * 2. Time Window Schedule
 * Shows playlist during specific time ranges on selected days
 */
export interface TimeWindowSchedule extends ScheduleBase {
  scheduleType: "TimeWindowSchedule";
  type: ScheduleType.TimeWindow;
  daysOfWeek: number[]; // 0=Sunday, 1=Monday, etc.
  startTime: string; // "HH:MM:SS"
  endTime: string; // "HH:MM:SS"
  displayDurationSeconds?: number | null;
}

/**
 * 3. Cross-Day Time Window Schedule
 * Shows playlist during time ranges that span multiple days
 */
export interface CrossDayTimeWindowSchedule extends ScheduleBase {
  scheduleType: "CrossDayTimeWindowSchedule";
  type: ScheduleType.CrossDayTimeWindow;
  startDay: number; // 0=Sunday, 1=Monday, etc.
  startTime: string; // "HH:MM:SS"
  endDay: number;
  endTime: string; // "HH:MM:SS"
  displayDurationSeconds?: number | null;
}

/**
 * 4. Prayer Window Schedule
 * Shows playlist between two prayer times on selected days
 */
export interface PrayerWindowSchedule extends ScheduleBase {
  scheduleType: "PrayerWindowSchedule";
  type: ScheduleType.PrayerWindow;
  daysOfWeek: number[];
  startPrayer: string; // PrayerName enum as string (e.g., "JamFajr")
  endPrayer: string; // PrayerName enum as string
  secondsBeforeStartPrayer?: number | null;
  secondsAfterStartPrayerEnds?: number | null;
  secondsBeforeEndPrayer?: number | null;
  displayDurationSeconds?: number | null;
}

/**
 * 5. Cross-Day Prayer Window Schedule
 * Shows playlist between prayer times that span across days
 */
export interface CrossDayPrayerWindowSchedule extends ScheduleBase {
  scheduleType: "CrossDayPrayerWindowSchedule";
  type: ScheduleType.CrossDayPrayerWindow;
  startDay: number;
  startPrayer: string; // PrayerName enum as string
  endDay: number;
  endPrayer: string; // PrayerName enum as string
  secondsBeforeStartPrayer?: number | null;
  secondsAfterStartPrayerEnds?: number | null;
  secondsBeforeEndPrayer?: number | null;
  displayDurationSeconds?: number | null;
}

/**
 * 6. Prayer-Based Schedule
 * Shows playlist before/after specific prayers
 */
export interface PrayerBasedSchedule extends ScheduleBase {
  scheduleType: "PrayerBasedSchedule";
  type: ScheduleType.PrayerBased;
  daysOfWeek: number[];
  prayerScheduleType:
    | "before"
    | "after"
    | "after-start"
    | "after-end"
    | "during-adhan"
    | "after-adhan"
    | "between";
  afterPhase?: "after-start" | "after-end" | null;
  prayers: string[]; // Array of PrayerName enum values as strings
  secondsBeforeStartPrayer?: number | null;
  secondsAfterStartPrayer?: number | null;
  displayDurationSeconds?: number | null;
  startPrayer: string; // Used for 'between' type
  endPrayer: string; // Used for 'between' type
}

/**
 * 7. Hijri Date Schedule
 * Shows playlist on specific Islamic calendar dates
 */
export interface HijriDateSchedule extends ScheduleBase {
  scheduleType: "HijriDateSchedule";
  type: ScheduleType.HijriDate;
  months: number[]; // 1-12 (Hijri months)
  days: string; // "1" or "1-10" or "1,15,27-29"
  time?: string | null; // "HH:MM:SS" optional
}

/**
 * 8. Gregorian Date Schedule
 * Shows playlist on specific Gregorian calendar dates
 */
export interface GregorianDateSchedule extends ScheduleBase {
  scheduleType: "GregorianDateSchedule";
  type: ScheduleType.GregorianDate;
  months: number[]; // 1-12 (Gregorian months)
  days: string; // "1" or "1-10" or "1,15,27-29"
  time?: string | null; // "HH:MM:SS" optional
}

/**
 * Event-based schedule - triggers based on specific events
 *
 * Azure Best Practice: Event-driven scheduling for special occasions
 */
export interface EventBasedSchedule extends ScheduleBase {
  scheduleType: "EventBasedSchedule";
  type: ScheduleType.EventBased;
  repeat: RepeatType;
  priority: PriorityLevel;

  /** Event identifier */
  eventId?: string;

  /** Event name/description */
  eventName?: string;

  /** Start date/time for event */
  eventStartTime?: string;

  /** End date/time for event */
  eventEndTime?: string;
}

// ============================================================================
// UNION TYPE FOR ALL SCHEDULES
// ============================================================================

export type Schedule =
  | AlwaysSchedule
  | TimeWindowSchedule
  | PrayerWindowSchedule
  | CrossDayTimeWindowSchedule
  | CrossDayPrayerWindowSchedule
  | HijriDateSchedule
  | GregorianDateSchedule
  | PrayerBasedSchedule
  | EventBasedSchedule; // ✅ Add this

// ============================================================================
// TYPE GUARDS
// ============================================================================

export const isAlwaysSchedule = (
  schedule: Schedule,
): schedule is AlwaysSchedule => {
  return schedule.scheduleType === "AlwaysSchedule";
};

export const isTimeWindowSchedule = (
  schedule: Schedule,
): schedule is TimeWindowSchedule => {
  return schedule.scheduleType === "TimeWindowSchedule";
};

export const isCrossDayTimeWindowSchedule = (
  schedule: Schedule,
): schedule is CrossDayTimeWindowSchedule => {
  return schedule.scheduleType === "CrossDayTimeWindowSchedule";
};

export const isPrayerWindowSchedule = (
  schedule: Schedule,
): schedule is PrayerWindowSchedule => {
  return schedule.scheduleType === "PrayerWindowSchedule";
};

export const isCrossDayPrayerWindowSchedule = (
  schedule: Schedule,
): schedule is CrossDayPrayerWindowSchedule => {
  return schedule.scheduleType === "CrossDayPrayerWindowSchedule";
};

export const isPrayerBasedSchedule = (
  schedule: Schedule,
): schedule is PrayerBasedSchedule => {
  return schedule.scheduleType === "PrayerBasedSchedule";
};

export const isHijriDateSchedule = (
  schedule: Schedule,
): schedule is HijriDateSchedule => {
  return schedule.scheduleType === "HijriDateSchedule";
};

export const isGregorianDateSchedule = (
  schedule: Schedule,
): schedule is GregorianDateSchedule => {
  return schedule.scheduleType === "GregorianDateSchedule";
};

export function isEventBasedSchedule(
  schedule: Schedule,
): schedule is EventBasedSchedule {
  return schedule.scheduleType === "EventBasedSchedule";
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get human-readable schedule type name
 */
export const getScheduleTypeName = (schedule: Schedule): string => {
  return schedule.scheduleType.replace("Schedule", "");
};

/**
 * Get human-readable priority name
 */
export const getPriorityName = (priority: PriorityLevel): string => {
  return PriorityLevel[priority];
};

/**
 * Get human-readable repeat type name
 */
export const getRepeatTypeName = (repeat: RepeatType): string => {
  return RepeatType[repeat];
};

/**
 * Parse days string (e.g., "1", "1-10", "1,15,27-29") into array of numbers
 */
export const parseDaysString = (daysStr: string): number[] => {
  const days: number[] = [];
  const parts = daysStr.split(",");

  for (const part of parts) {
    const trimmedPart = part.trim();
    if (trimmedPart.includes("-")) {
      const [start, end] = trimmedPart.split("-").map(Number);
      for (let i = start; i <= end; i++) {
        days.push(i);
      }
    } else {
      days.push(Number(trimmedPart));
    }
  }

  return days;
};

/**
 * Get day name from day number (0=Sunday)
 */
export const getDayName = (day: number): string => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return days[day] || "Unknown";
};

/**
 * Get month name from Gregorian month number (1-12)
 */
export const getGregorianMonthName = (month: number): string => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  return months[month - 1] || "Unknown";
};

/**
 * Get month name from Hijri month number (1-12)
 */
export const getHijriMonthName = (month: number): string => {
  const months = [
    "Muharram",
    "Safar",
    "Rabi al-Awwal",
    "Rabi al-Thani",
    "Jumada al-Awwal",
    "Jumada al-Thani",
    "Rajab",
    "Shaban",
    "Ramadan",
    "Shawwal",
    "Dhul-Qadah",
    "Dhul-Hijjah",
  ];
  return months[month - 1] || "Unknown";
};
